import * as XLSX from "xlsx";

export enum ExportFormat {
  CSV = "csv",
  Excel = "xlsx"
}

/** The name a table export downloads under. The browser numbers repeats itself. */
export const EXPORT_FILE_NAME = "EVA Export";

export const exportFileName = (format: ExportFormat): string =>
  `${EXPORT_FILE_NAME}.${format}`;

const REVOKE_DELAY_MS = 40 * 1000;

export const exportFile = (response: Blob | MediaSource, fileName: string) => {
  const encodedUri = URL.createObjectURL(response);
  const link = document.createElement("a");
  link.href = encodedUri;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  // Released late: Firefox and Safari read a large blob after the click, so an early
  // revoke breaks the download. FileSaver waits as long.
  setTimeout(() => URL.revokeObjectURL(encodedUri), REVOKE_DELAY_MS);
};

/**
 * Converts a date string to Excel date serial number
 * Excel serial date is the number of days since December 30, 1899
 * Sets the date to the 1st of the month
 */
function convertToExcelSerial(value: unknown): number | null {
  if (typeof value === "string" && value.includes("T")) {
    const date = new Date(value);

    // Excel epoch is December 30, 1899
    const excelEpoch = new Date(Date.UTC(1899, 11, 30, 0, 0, 0, 0));

    // Calculate days difference
    const daysDiff = (date.getTime() - excelEpoch.getTime()) / (1000 * 60 * 60 * 24);

    return daysDiff;
  }
  return null;
}

/**
 * Formats a date string by removing timezone info (YYYY-MM-DD format)
 * Always sets the day to the 1st of the month
 */
function formatDateForExport(value: unknown): string {
  if (typeof value === "string" && value.includes("T")) {
    const date = new Date(value);
    const year = date.getUTCFullYear();
    const month = (date.getUTCMonth() + 1).toString().padStart(2, "0");
    return `${year}-${month}-01`;
  }
  return String(value);
}

/**
 * Converts array of objects to CSV string
 */
function convertToCSV<T extends Record<string, unknown>>(
  data: T[],
  headers: { key: string; label: string }[],
  typeWellTitle: string
): string {
  const rows: string[] = [];

  // Add header row with UWI as first column
  const headerRow = ["UWI", ...headers.map((h) => h.label)].join(",");
  rows.push(headerRow);

  // Add data rows with type well title as first column
  const dataRows = data.map((row) => {
    const rowValues = [
      typeWellTitle,
      ...headers.map((h) => {
        const value = row[h.key];
        // Treat null/undefined/0 as blank (product present means non-zero value)
        if (value == null || value === 0) return "";

        const stringValue =
          h.key === "prodDate" ? formatDateForExport(value) : String(value);

        if (
          stringValue.includes(",") ||
          stringValue.includes('"') ||
          stringValue.includes("\n")
        ) {
          return `"${stringValue.replace(/"/g, '""')}"`;
        }
        return stringValue;
      })
    ];
    return rowValues.join(",");
  });

  rows.push(...dataRows);
  return rows.join("\n");
}

/**
 * Exports data to CSV format
 */
export function exportToCSV<T extends Record<string, unknown>>(
  data: T[],
  headers: { key: string; label: string }[],
  fileName: string,
  typeWellTitle: string
): void {
  const csv = convertToCSV(data, headers, typeWellTitle);
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  exportFile(blob, `${fileName}.csv`);
}

/**
 * Exports data to Excel format using xlsx library
 */
export function exportToExcel<T extends Record<string, unknown>>(
  data: T[],
  headers: { key: string; label: string }[],
  fileName: string,
  typeWellTitle: string
): void {
  const rows: (string | number | Date | null)[][] = [];

  // Add header row with UWI as first column
  rows.push(["UWI", ...headers.map((h) => h.label)]);

  const dateColumnIndices: number[] = [];
  headers.forEach((h, idx) => {
    if (h.key === "prodDate") {
      dateColumnIndices.push(idx + 1);
    }
  });

  // Add data rows with type well title as first column
  data.forEach((row) => {
    const dataRow = [
      typeWellTitle,
      ...headers.map((h) => {
        const value = row[h.key];
        // Treat null/undefined/0 as blank (Excel will show as empty cell with null)
        if (value == null || value === 0) return null;
        if (h.key === "prodDate") {
          // Convert to Excel serial number for proper date handling
          return convertToExcelSerial(value);
        }
        return value as string | number;
      })
    ];
    rows.push(dataRow);
  });

  // Create worksheet and workbook
  const worksheet = XLSX.utils.aoa_to_sheet(rows);

  // Set column widths (UWI column + all other columns)
  worksheet["!cols"] = [{ wpx: 120 }, ...headers.map(() => ({ wpx: 90 }))];

  // Format date columns with yyyy-mm-dd
  dateColumnIndices.forEach((colIdx) => {
    data.forEach((_, rowIdx) => {
      const cellAddress = XLSX.utils.encode_cell({ r: rowIdx + 1, c: colIdx }); // +1 for header row
      if (worksheet[cellAddress] && worksheet[cellAddress].t === "n") {
        // Excel stores dates as numbers, apply date format
        worksheet[cellAddress].z = "yyyy-mm-dd";
      }
    });
  });

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "Forecast Array");

  // Generate Excel file and trigger download
  XLSX.writeFile(workbook, `${fileName}.xlsx`);
}
